'use strict';
//***********************************************************************************
//***********************************************************************************
//******     CN-Map    **************************************************************
//******     Copyright(C) 2019-2020 EnerBIM                        ******************
//***********************************************************************************
//***********************************************************************************

//***********************************************************************************
//***********************************************************************************
//**** Beam and Column type
//***********************************************************************************
//***********************************************************************************

import { cn_element_type } from './cn_element_type';
import { cn_element_type_visitor } from '..';
import { cn_constant, NICHE_SHAPE_LIST } from './cn_model_constants';

/**
 * @class cn_niche_type
 * Common class type for beams and columns
 */
export class cn_niche_type extends cn_element_type {
    //***********************************************************************************
    /**
     * Constructor
     */
    constructor(parent) {
        super(parent);
        this.class_name = 'cn_niche_type';
        this.name = '';
        this.shape = 'rectangle';
        this.readonly = true;
    }

    //***********************************************************************************
    /**
     * Creates a default beam type
     * @returns {cn_niche_type[]}
     */
    static default_types(parent) {
        const def = [];

        const rect = new cn_niche_type(parent);
        rect.shape = 'rectangle';
        def.push(rect);

        const round = new cn_niche_type(parent);
        round.shape = 'round';
        def.push(round);

        const triangle = new cn_niche_type(parent);
        triangle.shape = 'triangle';
        def.push(triangle);

        return def;
    }

    //***********************************************************************************
    get_generic_label() {
        return 'Type de niche';
    }

    //***********************************************************************************
    /**
     * Returns a clone of this
     * @returns {cn_niche_type}
     */
    clone() {
        const c = new cn_niche_type(this.parent);
        c.shape = this.shape;

        return c;
    }

    //***********************************************************************************
    /**
     * Returns the list of keys of the element
     * @returns {string[]}
     */
    model_keys() {
        return ['shape'];
    }

    //***********************************************************************************
    /**
     * Serialize
     * @returns {object}
     */
    serialize() {
        const json = this.serialize_element();
        json.class_name = 'cn_niche_type';
        json.shape = this.shape;

        return json;
    }

    //***********************************************************************************
    /**
     * Unserialize
     * @param {object} json
     * @returns {cn_niche_type | false}
     */
    static unserialize(json, parent) {
        const ot = new cn_niche_type(parent);
        ot.unserialize_element(json);
        if (typeof (json.shape) == 'string' && cn_constant.code_to_label(json.shape, NICHE_SHAPE_LIST)) ot.shape = json.shape;
        return ot;
    }

    //***********************************************************************************
    /**
     * Returns a displayble label
     * @returns {string}
     */
    get_label() {
        if (this.name !== '') return this.name;
        let html = cn_constant.code_to_label(this.shape, NICHE_SHAPE_LIST);
        // @ts-ignore
        return html;
    }

    //***********************************************************************************
    /**
     * Returns an array of objects describing the type.
     * @returns {{label: string, value?: any, decimals?: number, unit?: string}[]}
     */
    get_description() {
        const description = [];

        const shape_label = cn_constant.code_to_label(this.shape, NICHE_SHAPE_LIST);
        if (shape_label)
            description.push({ label: 'Type', value: shape_label });

        return description;
    }

    //***********************************************************************************
    /**
     * Returns a html string to be put in a <svg>
     * @param {number} w : width of svg element
     * @param {number} h : height of svg element
     * @returns {string}
     */
    draw_svg_icon(w, h) {
        let html = ``;

        const ow = 1;
        const oh = 1.5;
        const sh = 0.5;
        const scale = (ow / oh > w / h) ? w / (1.05 * ow) : h / (1.05 * oh);

        const x0 = w / 2 - ow * scale / 2;
        const x1 = w / 2 + ow * scale / 2;
        const y1 = h / 2 - oh * scale / 2;
        const y0 = h / 2 + oh * scale / 2;
        const y2 = h / 2 - (oh - sh) * scale / 2;

        if (this.shape === 'rectangle') {
            html += `<rect x="${x0}" y="${y1}" width="${x1 - x0}" height="${y0 - y1}" `;
        } else if (this.shape === 'round') {
            html += `<path d="M`;
            html += ` ${x0} ${y0}`;
            html += ` L ${x1} ${y0}`;
            for (var theta=0;theta<=180;theta+=5) {
                let cosa = Math.cos(theta * Math.PI / 180) * (x1-x0) /2;
                let sina = Math.sin(theta * Math.PI / 180) * (y2-y1);
                let cx = (x0+x1)*0.5;
                html += ` L ${cx + cosa} ${y2 - sina}`;
            }
            html += `Z" `;
        } else if (this.shape === 'triangle') {
            html += `<path d="M`;
            html += ` ${x0} ${y0}`;
            html += ` L ${x1} ${y0}`;
            html += ` L ${x1} ${y2}`;
            html += ` L ${0.5*(x0+x1)} ${y1}`;
            html += ` L ${x0} ${y2}`;
            html += `Z" `;
        }

        html += ` class="niche" style="stroke:black"  />`;
        return html;
    }

    /**
     * Accept element type visitor
     *
     * @param {cn_element_type_visitor} element_type_visitor
     */
    accept_visitor(element_type_visitor) {
        element_type_visitor.visit_niche_type(this);
    }
}

